import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { VenueSnapshot, FranchiseAlert, FranchiseAlertType } from '@/types/franchise';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  X,
  ChevronRight,
  Calendar,
  Star,
  Users,
  Clock,
  Bell,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';

interface AlertBannerProps {
  venues: VenueSnapshot[];
  showAll?: boolean;
  maxAlerts?: number;
}

const alertIcons: Record<FranchiseAlertType, React.ElementType> = {
  schedule_gap: Calendar,
  review_alert: Star,
  staffing_shortage: Users,
  payroll_deadline: Clock,
  compliance_issue: AlertCircle,
  time_tracking: Clock,
  performance_drop: AlertTriangle,
};

const severityColors = {
  critical: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800',
    icon: 'text-red-600',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  warning: {
    bg: 'bg-orange-50 dark:bg-orange-950/30',
    border: 'border-orange-200 dark:border-orange-800',
    icon: 'text-orange-600',
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-800',
    icon: 'text-blue-600',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
};

const AlertBanner: React.FC<AlertBannerProps> = ({
  venues,
  showAll = false,
  maxAlerts = 3,
}) => {
  const isMobile = useIsMobile();
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  // Generate alerts from venue data
  const alerts = useMemo(() => {
    const generatedAlerts: FranchiseAlert[] = [];

    venues.forEach(venue => {
      // Schedule gap alerts
      if (venue.scheduleStatus === 'red') {
        generatedAlerts.push({
          id: `schedule-${venue.businessId}`,
          type: 'schedule_gap',
          severity: 'critical',
          businessId: venue.businessId,
          businessName: venue.businessName,
          title: 'Unassigned Shifts',
          description: `${venue.businessName} has shifts that need to be assigned`,
          actionRequired: true,
          createdAt: new Date(),
        });
      } else if (venue.scheduleStatus === 'orange') {
        generatedAlerts.push({
          id: `schedule-partial-${venue.businessId}`,
          type: 'schedule_gap',
          severity: 'warning',
          businessId: venue.businessId,
          businessName: venue.businessName,
          title: 'Partial Coverage',
          description: `${venue.businessName} has some shifts needing attention`,
          actionRequired: true,
          createdAt: new Date(),
        });
      }

      // Low rating alerts
      if (venue.averageRating > 0 && venue.averageRating < 3.5) {
        generatedAlerts.push({
          id: `rating-${venue.businessId}`,
          type: 'review_alert',
          severity: venue.averageRating < 3 ? 'critical' : 'warning',
          businessId: venue.businessId,
          businessName: venue.businessName,
          title: 'Low Rating',
          description: `${venue.businessName} has a rating of ${venue.averageRating.toFixed(1)}`,
          actionRequired: true,
          createdAt: new Date(),
        });
      }

      // Staffing shortage (no employees on shift during open hours)
      if (venue.isOpen && venue.employeesOnShift === 0 && venue.employeeCount > 0) {
        generatedAlerts.push({
          id: `staffing-${venue.businessId}`,
          type: 'staffing_shortage',
          severity: 'critical',
          businessId: venue.businessId,
          businessName: venue.businessName,
          title: 'No Staff On Shift',
          description: `${venue.businessName} is open but no employees are clocked in`,
          actionRequired: true,
          createdAt: new Date(),
        });
      }

      // Critical venue status
      if (venue.status === 'critical') {
        generatedAlerts.push({
          id: `status-${venue.businessId}`,
          type: 'performance_drop',
          severity: 'critical',
          businessId: venue.businessId,
          businessName: venue.businessName,
          title: 'Venue Needs Attention',
          description: `${venue.businessName} requires immediate attention`,
          actionRequired: true,
          createdAt: new Date(),
        });
      }
    });

    // Sort by severity (critical first)
    return generatedAlerts.sort((a, b) => {
      const severityOrder = { critical: 0, warning: 1, info: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    });
  }, [venues]);

  // Filter out dismissed alerts
  const visibleAlerts = alerts.filter(alert => !dismissedAlerts.has(alert.id));

  // Limit alerts if not showing all
  const displayedAlerts = showAll ? visibleAlerts : visibleAlerts.slice(0, maxAlerts);

  const dismissAlert = (alertId: string) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
  };

  // No alerts - show success state
  if (visibleAlerts.length === 0) {
    return (
      <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-200">All Clear</p>
              <p className="text-sm text-green-600 dark:text-green-400">
                No issues detected across your portfolio
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (showAll) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            Alert Center
            <Badge className="ml-2">
              {visibleAlerts.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {displayedAlerts.map(alert => (
            <AlertItem
              key={alert.id}
              alert={alert}
              onDismiss={() => dismissAlert(alert.id)}
              isMobile={isMobile}
            />
          ))}
        </CardContent>
      </Card>
    );
  }

  // Compact banner for overview
  return (
    <div className="space-y-2">
      {displayedAlerts.map(alert => (
        <AlertItem
          key={alert.id}
          alert={alert}
          onDismiss={() => dismissAlert(alert.id)}
          isMobile={isMobile}
          compact
        />
      ))}
      {visibleAlerts.length > maxAlerts && (
        <p className="text-sm text-muted-foreground text-center">
          +{visibleAlerts.length - maxAlerts} more alerts
        </p>
      )}
    </div>
  );
};

interface AlertItemProps {
  alert: FranchiseAlert;
  onDismiss: () => void;
  isMobile: boolean;
  compact?: boolean;
}

const AlertItem: React.FC<AlertItemProps> = ({ alert, onDismiss, isMobile, compact }) => {
  const Icon = alertIcons[alert.type];
  const colors = severityColors[alert.severity];

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg border",
        colors.bg,
        colors.border,
        compact ? "py-2" : ""
      )}
    >
      <Icon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", colors.icon)} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{alert.title}</span>
          <Badge className={cn("text-xs", colors.badge)}>
            {alert.businessName}
          </Badge>
        </div>
        {!compact && (
          <p className="text-sm text-muted-foreground mt-0.5">
            {alert.description}
          </p>
        )}
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 flex-shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default AlertBanner;
